<template>
    <div ref="content-builder-ai-modal" class="app-modal-dialog app-modal-dialog--editor-ai-relay" tabindex="-1" v-if="aiMode">
        <div class="app-modal-dialog__content modal-dialog">
            <div class="app-modal-dialog__header">
                <div class="app-modal-dialog__titles">
                    <div class="app-tab-navigation">
                        <div v-if="sessionStarted" class="app-tab-navigation__breadcrumbs">
                            <button
                                @click="reset()"
                                :disabled="isProcessing"
                                type="button"
                                class="app-tab-navigation__breadcrumb">
                                <svg class="element-icon--breadcrumb element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-breadcrumb"></use></svg>
                                Select Session
                            </button>
                            <svg class="element-icon--chevron-right element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-chevron-right"></use></svg>
                            <div class="app-tab-navigation__breadcrumb is--active">Current Session</div>
                        </div>
                    </div>
                <h2 class="app-modal-dialog__title">Script with AI</h2>
                </div>
            </div>
            <div class="app-modal-dialog__body">

                <div class="grid" v-if="!sessionStarted">
                    <div class="col-12 col-sm-6 col-md-4" v-if="hasExistingSession">
                        <button v-if="!sessionStarted"
                            @click="continueExistingSession()"
                            class="element-button--secondary element-button app-modal-dialog__editor-ai-relay-session-button"
                            title="Resume previous session">
                            <img class="app-modal-dialog__ai-continue-previous-session" src="/img/undraw_messaging_1s2k.svg" />
                            Continue previous session
                            <span>Pick up where you left off in conversation with the tool.</span>
                        </button>
                    </div>
                    <div class="col-12 col-sm-6 col-md-4" v-if="hasPublication">
                        <button v-if="hasPublication && !sessionStarted"
                            @click="startNewPublicationBasedSession(currentDataObject)"
                            class="element-button--secondary element-button app-modal-dialog__editor-ai-relay-session-button"
                            title="Continue based on existing outline">
                            <img class="app-modal-dialog__ai-continue-existing-outline" src="/img/undraw_timeline_2gfy.svg" />
                            Continue from AI outline
                            <span>Script with context of uploaded file(s) and AI-generated outline.</span>
                        </button>
                    </div>
                    <div class="col-12 col-sm-6 col-md-4">
                        <button v-if="!sessionStarted"
                            @click="startNewInternetSession(currentDataObject)"
                            class="element-button--secondary element-button app-modal-dialog__editor-ai-relay-session-button"
                            title="Originate new AI content">
                            <img class="app-modal-dialog__ai-originate-new-content" src="/img/undraw_lightbulb-moment_c8jn.svg" />
                            Originate new content
                            <span>Work without any uploaded files or AI-generated outline.</span>
                        </button>
                    </div>
                </div>

                <form class="app-content-builder-ai-relay" @submit.prevent="execute(currentDataObject)" v-if="sessionStarted">
                    <ai-relay-history
                        @removeItemFromHistory="onHistoryItemRemove($event)"
                        :source="history">
                    </ai-relay-history>
                    <div class="app-modal-dialog__ai-relay-input-group">
                        <div class="input textarea">
                            <textarea
                                type="textarea"
                                rows="5"
                                v-model="command"
                                @keydown="onTextAreaKeyDown($event, currentDataObject)"
                                ref="input"></textarea>
                        </div>
                        <button
                            @click="execute(currentDataObject)"
                            :disabled="!command || isProcessing"
                            type="button"
                            class="element-button--primary element-button">
                            <svg class="element-icon--send element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-send"></use></svg>
                        </button>
                    </div>
                </form>
                <div class="app-modal-dialog__ai-prompts" v-if="sessionStarted">
                    <!--button
                        @click="validateJson(currentDataObject)"
                        :disabled="isProcessing"
                        type="button"
                        class="btn btn-outline-secondary">
                        I'm Ready, validate data
                    </button-->
                    
                    <div v-if="aiProjectPrompts && aiProjectPrompts.length > 0" class="input select">
                        <select
                            v-model="selectedType" :disabled="isProcessing" @change="onSelectPromptDropdown($event)">
                            <option value="default" selected>Select prompt to insert</option>
                            <option v-for="aiProjectPrompt in aiProjectPrompts"
                                :key="`aiPrompt-${aiProjectPrompt.id}`"
                                :title="aiProjectPrompt.message"
                                :value="aiProjectPrompt.message">
                                {{ aiProjectPrompt.label }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
            <div class="app-modal-dialog__footer">
                <button type="button"
                    aria-label="Close"
                    @click="dismissOverlay()"
                    class="element-button--secondary element-button">
                    Cancel
                </button>
                <app-button v-if="sessionStarted && !jsonValidated"
                    @click="appendJsonToPrompt()"
                    title="Insert screen content (json) into chat window"
                    class="element-button--primary element-button"
                    :disabled="isProcessing">
                    Paste Screen Contents
                </app-button>
                <button v-if="sessionStarted"
                    @click="saveJson(currentDataObject)"
                    :disabled="!jsonValidated || isProcessing"
                    type="button"
                    class="element-button--primary element-button">
                    Update in Editor
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'
import api from '../api/ai'
import EventBus from '../helpers/event-bus'
import AiRelayHistory from './AiRelayHistory'

export default {
    components: {
        AiRelayHistory
    },
    props: {
        aiProjectPrompts: {
            type: Array,
            default: null
        },
    },
    data () {
        return {
            command: '',
            sessionStarted: false,
            isProcessing: false,
            jsonValidated: false,
            sessionId: null,
            screenJson: null,
            publicationId: null,
            currentJson: null,
            history: [],
            selectedType: 'default'
        }
    },
    computed: {
        ...mapState('builder', {
            source: state => state.source,
            settings: state => state.settings,
            selectedId: state => state.selectedId
        }),
        ...mapGetters('builder', [
            'currentDataObject',
            'aiPublications'
        ]),
        aiMode: {
            get () {
                return this.$store.state.builder.aiMode
            },
            set (value) {
                this.$store.commit('builder/setAiMode', value)
            }
        },
        hasPublication () {
            return (this.aiPublications && this.aiPublications.length)
        },
        hasExistingSession () {
            return this.currentDataObject?.latest_ai_session ? true : false
        },
        currentSession () {
            return this.currentDataObject?.latest_ai_session ?? null
        }
    },
    watch: {
        selectedId (value) {
            // if screen changes, reset everything
            this.reset()
        },
        sessionId (value) {
            if (value)
                this.setAiMetadata()
        },
        aiMode (value) {
            if (value)
            {
                if (this.currentJson && this.currentDataObject.data && this.currentDataObject.data != this.currentJson)
                {
                    // alert('UNSAVED DATA')
                    console.log('DataObject contains unsaved data')
                }
            }
        },
    },
    methods: {
        dismissOverlay () {
            this.aiMode = false
        },
        reset() {
            this.isProcessing = false
            this.sessionStarted = false
            this.sessionId = false
            this.jsonValidated = false
            this.screenJson = false
            this.command = ''
            this.publicationId = null
            this.history = []
        },
        appendJsonToPrompt() {
            const _data = this.currentDataObject.data ?? []
            if (!_data) {
                alert('Screen json is empty, save before generating')
                return false
            }
            const _j = JSON.stringify(_data, null, 2)
            const str = this.currentDataObject.title + '\n' + _j

            this.command += str
        },
        onTextAreaKeyDown (event, dataObject) {
            // Check if the 'Enter' key was pressed
            if (event.key === 'Enter') {
                // If 'Shift' is not being held down, prevent the default behavior
                if (!event.shiftKey) {
                    event.preventDefault()
                    this.execute(dataObject)
                }
            }
        },
        onSelectPromptDropdown (event) {
            this.command += event.target.value;
            this.selectedType = 'default';        
        },
        async onHistoryItemRemove (messageIds) {
            console.log('remove messages')
            console.log(messageIds)

            try {
                var params = { messages: messageIds };

                const response = await api.removeMessages(this.sessionId, params)
                if (response?.status === 'error')
                {
                    console.error(response)
                    alert(response?.errors)
                }
                else
                {
                    console.log(response)
                    this.history = response.messages
                }
            } catch (err) {
                console.error(err.response)
                alert('unable to remove messages')
            }
        },
        async continueExistingSession () {

            if (!this.currentSession)
                return false

            this.isProcessing = true
            this.sessionStarted = true
            this.sessionId = this.currentSession.guid

            const publication = this.aiPublications[0] ?? null

            if (publication)
                this.publicationId = publication.id

            let item = {
                user_query: '',
                ai_response: 'Retrieving AI session, please wait',
                loading: true
            }

            this.history.push(item)

            let response = await api.getMessages(this.sessionId)

            if (response && response?.messages)
                this.history = response?.messages

            this.isProcessing = false
        },
        initializeSession () {
            this.isProcessing = true
            this.sessionStarted = true
            this.sessionId = null

            let item = {
                user_query: '',
                ai_response: 'Initializing AI session, please wait',
                loading: true
            }

            this.history.push(item)
        },
        async startNewInternetSession (source) {
            console.log('Start new Internet only session')

            this.publicationId = null
            this.currentJson = source.data

            this.initializeSession();

            try {
                var params = { };

                const response = await api.initializeScreen(`${source.id}`, params)
                if (response?.status === 'error')
                {
                    console.error(response)
                    alert(response?.errors)
                    this.reset();
                }
                else
                {
                    this.history = response.latest_ai_session.messages
                    this.sessionId = response.latest_ai_session.guid
                }
            } catch (err) {
                // TODO: handle error
                // replace last item with error message
                console.error(err.response)
            }

            this.isProcessing = false
        },
        async startNewPublicationBasedSession (source) {
            console.log('Start new Publication based session')

            this.initializeSession();

            const publication = this.aiPublications[0] ?? null

            if (publication)
            {
                this.publicationId = publication.id
                try {
                    var params = { };

                    const response = await api.initializeScreenWithPublication(`${source.id}`, `${publication.id}`, params)
                    if (response?.status === 'error')
                    {
                        console.error(response)
                        alert(response?.errors)
                        this.reset();
                    }
                    else
                    {
                        console.log('OK')
                        console.log(response)
                        this.history = response.latest_ai_session.messages
                        this.sessionId = response.latest_ai_session.guid
                    }
                } catch (err) {
                    // TODO: handle error
                    // replace last item with error message
                    console.error(err.response)
                }
            }
            else
            {
                // TODO: no linked publication
            }

            this.isProcessing = false

            console.log(publication)
        },
        async execute (dataObject, _command, messageType) {

            if (this.isProcessing)
                return false

            const command = _command ?? this.command

            if (!command || command.trim() === '')
                return false;

            if (!this.sessionId)
            {
                alert('you do not have a valid session, please reinitialize')
                return false
            }

            this.command = ''
            this.isProcessing = true
            this.jsonValidated = false

            const item = {
                user_query: command,
                ai_response: 'Waiting on response',
                loading: true
            }

            this.history.push(item)

            const data = {
                sessionId:`${this.sessionId}`,
                prompt:command,
            }
            if (this.publicationId)
                data.publicationId = this.publicationId
            if (messageType)
                data.messageType = messageType

            let response = false;
            try {
                response = await api.executeScreen(`${dataObject.id}`, data)
                console.log(response)
                if (response?.status === 'error')
                {
                    console.error(response)
                    alert(response?.errors)
                }
                else
                {
                    if (response) {
                        if (response?.latest_ai_session?.messages)
                        {
                            this.history = response.latest_ai_session.messages;
                            try {
                                const _jsonStr = _.last(this.history)?.ai_response ?? null
                                this.checkForScreenJsonResponse(_jsonStr)
                            }
                            catch (err) { }
                        }
                    }
                }
            } catch (err) {
                // TODO: handle error
                // replace last item with error message
                console.error(err.response)
            }
            this.isProcessing = false

            return response
        },
        async checkForScreenJsonResponse (_jsonStr) { // validate if the response is in a json format and matches a few possible properties that our drivers share 
            let _json = null
            if (_jsonStr)
            {
                try {
                    _json = JSON.parse(_jsonStr)
                } catch (err) {
                    _json = null
                }
            }

            if (_json)
            {
                if (_json?.settings || _json?.options || _json?.eventHooks) // validate common properties in skeleton and phoenix
                {
                    this.jsonValidated = true
                    this.screenJson = _json
                }
            }
        },
        async validateJson (dataObject) {
            const command = this.settings['ai-prompt--screen-json'] ?? '';

            if (!command)
            {
                alert('something went wrong, try again')
                return false
            }

            const result = await this.execute(dataObject, command);

            console.log(result)
            if (result !== false)
            {
                const messages = result?.latest_ai_session?.messages
                const _jsonStr = _.last(messages)?.ai_response ?? null

                let _json = null
                if (_jsonStr)
                {
                    try {
                        _json = JSON.parse(_jsonStr)
                    } catch (err) {
                        _json = null
                    }
                }

                console.log('JSON:')
                console.log(_json)
                if (_json)
                {
                    this.jsonValidated = true
                    this.screenJson = _json
                }
                else
                {
                    console.log('Unable to validate the json content')
                }
            }
            else // TODO: handle error
            { }
        },
        async saveJson (dataObject) {
            if (this.screenJson)
            {
                dataObject.data = this.screenJson
                this.$store.commit('builder/updateDataObject', dataObject)
                EventBus.$emit('editor:setValue', dataObject.data)
                this.aiMode = false
            }
        },
        setAiMetadata () {
            console.log('[Set _aiMetadata]')
            window._aiMetadata = {
                publicationId: this.publicationId || null,
                dataObjetId: this.selectedId || null,
                sessionId: this.sessionId,
                nonLite: true
            }
        },
        clearAiMetadata () {
            console.log('[Clear _aiMetadata]')
            window._aiMetadata = null
        }
    },
    
    mounted () {
        if (this.sessionId)
            this.setAiMetadata()
    },
    beforeUnmount () {
        this.clearAiMetadata()
    }
}
</script>
